import React, { useState, useEffect } from 'react';
import { api } from './lib/api';

interface Template {
  id: string;
  type: 'recette' | 'depense';
  compte_principal: string;
  ttc_centimes: number;
  libelle: string;
  piece_ref?: string;
  tva_taux?: number | null;
  frequency: 'monthly' | 'quarterly' | 'yearly';
  next_date: string;
  is_active: boolean;
}

interface RecurringTemplatesProps {
  entrepriseId: string;
  exerciceId: string;
}

const frequencyLabel: Record<string, string> = { monthly: 'Mensuelle', quarterly: 'Trimestrielle', yearly: 'Annuelle' };

const RecurringTemplates: React.FC<RecurringTemplatesProps> = ({ entrepriseId, exerciceId }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'depense' as 'recette' | 'depense',
    compte_principal: '',
    ttc_centimes: '',
    libelle: '',
    piece_ref: '',
    tva_taux: '',
    frequency: 'monthly' as const,
    next_date: new Date().toISOString().split('T')[0],
    is_active: true,
  });

  const fetchTemplates = async () => {
    try {
      const data = await api.recurring.list();
      setTemplates(data);
    } catch {}
  };

  useEffect(() => { fetchTemplates(); }, []);

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      entreprise_id: entrepriseId, // use the prop
      ttc_centimes: Number(form.ttc_centimes),
      tva_taux: form.tva_taux ? Number(form.tva_taux) : null,
      next_date: new Date(form.next_date).toISOString(),
    };
    try {
      await api.recurring.create(payload);
      setForm({ type: 'depense', compte_principal: '', ttc_centimes: '', libelle: '', piece_ref: '', tva_taux: '', frequency: 'monthly', next_date: new Date().toISOString().split('T')[0], is_active: true });
      setShowForm(false);
      fetchTemplates();
    } catch {
      alert('Erreur création');
    }
  };

  const generate = async (id: string) => {
    try {
      await api.recurring.generate(id, exerciceId);
      alert('Transaction créée');
      fetchTemplates();
    } catch {
      alert('Erreur génération');
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Transactions récurrentes</h2>
      <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors mb-4">
        {showForm ? 'Annuler' : 'Nouveau modèle'}
      </button>

      {showForm && (
        <form onSubmit={createTemplate} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className={labelClass}>Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as 'recette' | 'depense' })} className={inputClass}>
                <option value="depense">Dépense</option>
                <option value="recette">Recette</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Compte PCG</label>
              <input type="text" value={form.compte_principal} onChange={e => setForm({ ...form, compte_principal: e.target.value })} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>TTC (€)</label>
              <input type="number" step="0.01" value={form.ttc_centimes} onChange={e => setForm({ ...form, ttc_centimes: e.target.value })} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Taux TVA (%)</label>
              <input type="number" step="0.1" value={form.tva_taux} onChange={e => setForm({ ...form, tva_taux: e.target.value })} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className={labelClass}>Libellé</label>
              <input type="text" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Référence pièce</label>
              <input type="text" value={form.piece_ref} onChange={e => setForm({ ...form, piece_ref: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Fréquence</label>
              <select value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value as any })} className={inputClass}>
                <option value="monthly">Mensuelle</option>
                <option value="quarterly">Trimestrielle</option>
                <option value="yearly">Annuelle</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Prochaine date</label>
              <input type="date" value={form.next_date} onChange={e => setForm({ ...form, next_date: e.target.value })} required className={inputClass} />
            </div>
          </div>
          <button type="submit" disabled={!form.compte_principal || !form.ttc_centimes || !form.libelle} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Créer modèle</button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">TTC (€)</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Fréquence</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Prochaine</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actif</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(t => (
              <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{t.libelle}</td>
                <td className="px-4 py-3 border-b border-gray-100">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${t.type === 'recette' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {t.type === 'recette' ? 'Recette' : 'Dépense'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{t.compte_principal}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right font-medium">{(t.ttc_centimes/100).toFixed(2)}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{frequencyLabel[t.frequency] || t.frequency}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{new Date(t.next_date).toLocaleDateString('fr-FR')}</td>
                <td className="px-4 py-3 border-b border-gray-100">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.is_active ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td className="px-4 py-3 border-b border-gray-100">
                  <button onClick={() => generate(t.id)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors">Générer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecurringTemplates;
